import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CheckCircle2, XCircle, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';

import { INITIATE_SUBSCRIPTION } from '../graphql/queries';
import { useMyStore } from '../hooks/useMyStore';

interface SubscriptionManageProps {
  // Kept for backwards compatibility with App.tsx — no longer used.
  userCount?: number;
  setUserCount?: (count: number) => void;
}

/**
 * Minimal subscription view. The external subscription portal owns plan
 * detail, invoices, and history — clicking "Manage Subscription" hands
 * the user a freshly-signed token and redirects there. We only display
 * whether the store is currently active.
 */
export const SubscriptionManage: React.FC<SubscriptionManageProps> = () => {
  const { store } = useMyStore();
  const isActive = !!store?.isSubscriptionActive;

  const [initiate, { loading }] = useMutation(INITIATE_SUBSCRIPTION);
  const [error, setError] = useState<string | null>(null);

  const openSubscriptionPortal = async () => {
    setError(null);
    try {
      const { data } = await initiate();
      const out = data?.initiateSubscription;
      if (out?.error || !out?.token || !out?.paymentPageUrl) {
        setError(out?.message || 'Could not open the subscription page');
        return;
      }
      const returnUrl = `${window.location.origin}${window.location.pathname}?page=subscription-verification`;
      window.location.href = `${out.paymentPageUrl}${out.token}?${returnUrl}`;
    } catch (err: any) {
      console.error('[SubscriptionManage.openSubscriptionPortal]', err);
      setError(err?.message || 'Could not open the subscription page');
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-black text-text">Subscription</h1>
        <p className="text-sm text-text2">
          Plan details, invoices and history live on the subscription portal — click below to open it.
        </p>
      </header>

      <section className="card p-6">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
              isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
            }`}
          >
            {isActive ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-text3 mb-1">
              Status
            </div>
            <div
              className={`text-lg font-black ${
                isActive ? 'text-emerald-700' : 'text-amber-700'
              }`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </div>
            <p className="text-xs text-text2 mt-1">
              {isActive
                ? 'Your store has an active subscription.'
                : 'Your store is currently inactive. Subscribe to unlock the dashboard.'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-5 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertTriangle size={14} />
            {error}
          </div>
        )}

        <button
          onClick={openSubscriptionPortal}
          disabled={loading}
          className="btn-primary w-full mt-6 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Opening…
            </>
          ) : (
            <>
              <ExternalLink size={14} />
              {isActive ? 'Manage Subscription' : 'Activate Subscription'}
            </>
          )}
        </button>
      </section>

      <p className="text-center text-[11px] text-text3">
        Plan changes and invoices are managed on the subscription portal. After completing payment
        you'll be redirected back here automatically.
      </p>
    </div>
  );
};
